import { Interval, afterDay } from "components/utils/comm";
import { IRmOp } from "interface/rm/op";
import { IRmInf } from "interface/rm/rm";
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";

// 객실상품 조회 Filters Context
export type initialFilterStateProps = {
  rmNo: string;
  rmOlkCd: string;
  rmTypCd: string;
  maxPerCtMax: string;
  maxPerCtMin: string;
  lodChlCd: string;
  rmPdNo: string;
  rmOpNm: string;
  sellSDt: string;
  sellEDt: string;
  sellYn: string;
};
interface ProductFilterContextType {
  filters: initialFilterStateProps;
  // initialFilterState: initialFilterStateProps;
  onChange: (name: string, value: string) => void;
  onSelect: (name: string, value: string) => void;
  onPickDate: (dates: Array<string>) => void;
  onReset: () => void;
}

const ProductFilterContext = createContext<
  ProductFilterContextType | undefined
>(undefined);

interface ProductFilterProviderProps {
  children: ReactNode;
}

export const ProductFilterProvider = ({
  children,
}: ProductFilterProviderProps) => {
  const { sDt, eDt } = afterDay(Interval);
  const initialFilterState = {
    rmNo: "",
    rmOlkCd: "",
    rmTypCd: "",
    maxPerCtMax: "",
    maxPerCtMin: "",
    lodChlCd: "",
    rmPdNo: "",
    rmOpNm: "",
    sellYn: "Y",
    sellSDt: sDt,
    sellEDt: eDt,
  };

  const [filters, setFilters] =
    useState<initialFilterStateProps>(initialFilterState);

  const onChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onSelect = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onPickDate = (dates: Array<string>) => {
    setFilters({
      ...filters,
      sellSDt: dates[0],
      sellEDt: dates[1],
    });
  };

  const onReset = () => {
    setFilters(initialFilterState);
  };

  return (
    <ProductFilterContext.Provider
      value={{
        filters,
        onChange,
        onSelect,
        onPickDate,
        onReset,
      }}
    >
      {children}
    </ProductFilterContext.Provider>
  );
};

export const useProductFilterContext = (): ProductFilterContextType => {
  const context = useContext(ProductFilterContext);

  if (!context) {
    throw new Error(
      "useProductFilterContext는 ProductFilterProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 객실상품 조회 Params Context
interface ProductParamsContextType {
  param: initialFilterStateProps | undefined;
  setParam: React.Dispatch<
    React.SetStateAction<initialFilterStateProps | undefined>
  >;
}

const ProductParamsContext = createContext<
  ProductParamsContextType | undefined
>(undefined);

interface ProductParamsProviderProps {
  children: ReactNode;
}

export const ProductParamsProvider = ({
  children,
}: ProductParamsProviderProps) => {
  const [param, setParam] = useState<initialFilterStateProps>();

  return (
    <ProductParamsContext.Provider
      value={{
        param,
        setParam,
      }}
    >
      {children}
    </ProductParamsContext.Provider>
  );
};

export const useProductParamsContext = (): ProductParamsContextType => {
  const context = useContext(ProductParamsContext);

  if (!context) {
    throw new Error(
      "useProductParamsContext는 ProductParamsProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 객실상품 생성 Params Context
export interface initialCreateParamsProps {
  lodNo: string;
  lodChlCd: string;
  rmNo: string;
  rmOpSeq: number;
  rmPdSDt: string;
  rmPdEDt: string;
  rmPdAplyDow: string;
  rmPdCt: number | string | undefined;
  rmPdSellPri: number | string | undefined;
}

interface CreatePrudoctParamContextType {
  param: initialCreateParamsProps;
  rmInf: IRmInf | null;
  opInf: IRmOp | null;
  onChange: (name: string, value: string) => void;
  onSelect: (name: string, value: string) => void;
  onPickDate: (dates: Array<string>) => void;
  onReset: () => void;
}

const CreatePrudoctParamContext = createContext<
  CreatePrudoctParamContextType | undefined
>(undefined);

interface CreatePrudoctParamProviderProps {
  children: ReactNode;
}

export const CreatePrudoctParamProvider = ({
  children,
}: CreatePrudoctParamProviderProps) => {
  const { sDt, eDt } = afterDay(90);
  const initialFilterState = {
    lodNo: "",
    lodChlCd: "",
    rmNo: "",
    rmOpSeq: 0,
    rmPdSDt: sDt,
    rmPdEDt: eDt,
    rmPdAplyDow: "YYYYYYY",
    rmPdCt: undefined,
    rmPdSellPri: undefined,
  };

  const [param, setParam] =
    useState<initialCreateParamsProps>(initialFilterState);

  const [rmInf, setRmInf] = useState<IRmInf | null>(null);
  const [opInf, setOpInf] = useState<IRmOp | null>(null);

  const onChange = (name: string, value: string) => {
    setParam({
      ...param,
      [name]: value,
    });
  };

  const onSelect = (name: string, value: string | any) => {
    if (name === "rmNo") {
      setParam({
        ...param,
        [name]: value.rmNo,
      });
      setRmInf(value);
      return;
    }
    if (name === "rmOpSeq") {
      setParam({
        ...param,
        [name]: value.rgSeq,
      });
      setOpInf(value);
      return;
    }
    setParam({
      ...param,
      [name]: value,
    });
  };

  const onPickDate = (dates: Array<string>) => {
    setParam({
      ...param,
      rmPdSDt: dates[0],
      rmPdEDt: dates[1],
    });
  };

  const onReset = () => {
    setParam(initialFilterState);
    setRmInf(null);
    setOpInf(null);
  };

  return (
    <CreatePrudoctParamContext.Provider
      value={{
        param,
        rmInf,
        opInf,
        onChange,
        onSelect,
        onPickDate,
        onReset,
      }}
    >
      {children}
    </CreatePrudoctParamContext.Provider>
  );
};

export const useCreatePrudoctParamContext =
  (): CreatePrudoctParamContextType => {
    const context = useContext(CreatePrudoctParamContext);

    if (!context) {
      throw new Error(
        "useCreatePrudoctParamContext는 CreatePrudoctParamProvider 내부에서만 사용해야 합니다."
      );
    }

    return context;
  };
